/**
 * Utility functions for array adjustments in recurrence forms
 */

/**
 * Default values for different array types
 */
export const ARRAY_DEFAULTS = {
  eventOccurrence: '',
  dayOfTheWeek: '',
  dayOfTheMonth: '',
}

/**
 * Adjusts an array to match the target length while preserving existing valid values
 *
 * @param {Array|*} currentValue - Current value (can be array or single value)
 * @param {number} targetLength - Desired array length
 * @param {*} defaultValue - Default value to fill new slots
 * @param {Function} isValidValue - Optional function to validate existing values
 * @returns {Array} New array with correct length
 */
export const adjustArrayToLength = (currentValue, targetLength, defaultValue, isValidValue = () => true) => {
  // Handle non-positive target lengths
  if (targetLength <= 0) {
    return []
  }

  // Create new array filled with defaults
  const newArray = Array(targetLength).fill(defaultValue)

  // Handle array input - preserve valid existing values
  if (Array.isArray(currentValue)) {
    const copyLength = Math.min(currentValue.length, targetLength)
    for (let i = 0; i < copyLength; i++) {
      const value = currentValue[i]
      if (value !== null && value !== undefined && isValidValue(value)) {
        newArray[i] = value
      }
    }
  }
  // Handle single value input - place in first position if valid
  else if (currentValue !== null && currentValue !== undefined && isValidValue(currentValue)) {
    newArray[0] = currentValue
  }

  return newArray
}

/**
 * Checks if an array needs adjustment (length mismatch or invalid structure)
 *
 * @param {Array|*} value - Value to check
 * @param {number} expectedLength - Expected array length
 * @returns {boolean} True if adjustment is needed
 */
export const needsArrayAdjustment = (value, expectedLength) => {
  return !Array.isArray(value) || value.length !== expectedLength
}

/**
 * Validates that a value is not empty/invalid for eventOccurrence
 *
 * @param {*} value - Value to validate
 * @returns {boolean} True if value is valid
 */
export const isValidEventOccurrence = value => {
  return value !== 0 && value !== undefined && value !== null && value !== ''
}

/**
 * Validates that a value is not empty/invalid for dayOfTheWeek
 *
 * @param {*} value - Value to validate
 * @returns {boolean} True if value is valid
 */
export const isValidDayOfTheWeek = value => {
  return value !== undefined && value !== null && value !== ''
}

/**
 * Creates array adjustment configuration for common form fields
 *
 * @param {number} frequency - Target frequency/length
 * @returns {Object} Configuration object with adjustment parameters
 */
export const createArrayAdjustmentConfig = frequency => ({
  eventOccurrence: {
    targetLength: frequency,
    defaultValue: ARRAY_DEFAULTS.eventOccurrence,
    validator: isValidEventOccurrence,
  },
  dayOfTheWeek: {
    targetLength: frequency,
    defaultValue: ARRAY_DEFAULTS.dayOfTheWeek,
    validator: isValidDayOfTheWeek,
  },
  dayOfTheMonth: {
    targetLength: frequency,
    defaultValue: ARRAY_DEFAULTS.dayOfTheMonth,
    validator: value => typeof value === 'number' && value > 0,
  },
})
